/**
 * Formulario reutilizable de Órdenes (alta + edición).
 *
 * Una sola pieza para crear y editar (`progress/explore_orders_ssr.md`, §J: el SSR
 * comparte `add_order`/`edit_order`). Lo orquestan dos pantallas:
 *  - `OrderNewScreen` (modo `create`): todos los campos editables; `onSubmit`
 *    llama a `useCreateOrder` (POST).
 *  - `OrderEditScreen` (modo `edit`): respeta los **permisos granulares por campo**
 *    (`FIELD_EDIT_PERMISSION`): un campo sin permiso se muestra solo-lectura y
 *    **no se envía** (la pantalla envía solo lo editable). `onSubmit` llama a
 *    `useUpdateOrder` (PATCH).
 *
 * Responsabilidades de ESTE componente (presentación + validación local):
 *  - Estado controlado de los campos (formato de UI: fechas `YYYY-MM-DD`, montos texto).
 *  - Validación con Zod (`orderFormSchema`) antes de delegar: si falla, muestra los
 *    errores por campo y no llama al `onSubmit`.
 *  - Campos condicionales: `amount_paid` solo si `payment_status==="abonado"`;
 *    `order_type` solo si la sede permite láser (`showOrderType`).
 *  - Mensajes de error de la API anclados a su campo (`fieldErrorFromApi`) o, si no
 *    mapean a un campo, como error general del formulario.
 *
 * No hace `fetch` ni decide permisos: recibe `canEditField`, `showOrderType`, el
 * estado de envío (`isSubmitting`) y el error de la API (`apiError`) desde la
 * pantalla, que es quien posee los hooks de datos. Usa `components/ui`.
 */
import { useMemo, useState } from "react";

import { Button, Input } from "@/components/ui";
import { ApiError } from "@/lib/api";

import {
  ERROR_CODE_FIELD,
  FIELD_LABEL,
  ORDER_ERROR_MESSAGE,
  PAYMENT_LABEL,
} from "../constants";
import {
  ORDER_TYPE_VALUES,
  PAYMENT_STATUS_VALUES,
  orderFormSchema,
  type OrderFormValues,
} from "../schemas";

/** Campo del formulario al que se puede anclar un error (incluye `amount_paid`). */
type FieldKey = keyof OrderFormValues;

/** Errores por campo derivados de Zod o del `error_code` de la API. */
type FieldErrors = Partial<Record<FieldKey, string>>;

interface OrderFormProps {
  mode: "create" | "edit";
  /** Valores iniciales (en formato de UI). En `create`, valores por defecto. */
  initialValues: OrderFormValues;
  /**
   * Decide si un campo es editable. En `create` siempre `true`; en `edit`,
   * refleja el permiso granular (`FIELD_EDIT_PERMISSION`). Los campos no
   * editables se pintan solo-lectura.
   */
  canEditField: (field: FieldKey) => boolean;
  /** Muestra el selector de tipo de orden (solo si la sede permite láser). */
  showOrderType: boolean;
  /** Estado de envío (deshabilita el submit y muestra "Guardando…"). */
  isSubmitting: boolean;
  /** Error de la API tras enviar (se traduce a mensaje legible). */
  apiError: unknown;
  /**
   * Envía el formulario. Recibe SOLO los campos editables (en `edit`, los no
   * editables se omiten para no enviarlos), ya validados por Zod.
   */
  onSubmit: (editableValues: Partial<OrderFormValues>) => void;
  /** Texto del botón de envío (p. ej. "Registrar" / "Actualizar"). */
  submitLabel: string;
  /** Cancela y vuelve al listado. */
  onCancel: () => void;
}

/** Estilo de un valor solo-lectura (campo sin permiso de edición). */
const READONLY_CLASS =
  "block w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700";

/** Estilo de un `<select>` coherente con `components/ui/Input`. */
const SELECT_CLASS =
  "block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-red-500";

/** Traduce el `error_code` de la API al campo del formulario y su mensaje. */
function fieldErrorFromApi(
  error: unknown,
): { field?: FieldKey | undefined; message: string } | null {
  if (!(error instanceof ApiError)) {
    if (error instanceof Error && error.message) {
      return { message: error.message };
    }
    return null;
  }
  const message = ORDER_ERROR_MESSAGE[error.error_code] ?? error.message;
  const field = ERROR_CODE_FIELD[error.error_code] as FieldKey | undefined;
  return { field, message };
}

export function OrderForm({
  mode,
  initialValues,
  canEditField,
  showOrderType,
  isSubmitting,
  apiError,
  onSubmit,
  submitLabel,
  onCancel,
}: OrderFormProps) {
  const [values, setValues] = useState<OrderFormValues>(initialValues);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // Error de la API: lo recalculamos en cada render del padre (apiError cambia con
  // la mutación). Si mapea a un campo, se ancla ahí; si no, es error general.
  const apiFieldError = useMemo(() => fieldErrorFromApi(apiError), [apiError]);
  const formLevelApiError =
    apiFieldError && apiFieldError.field === undefined ? apiFieldError.message : null;

  /** Mensaje de error a mostrar bajo un campo (Zod local o API). */
  function errorFor(field: FieldKey): string | undefined {
    if (fieldErrors[field]) {
      return fieldErrors[field];
    }
    if (apiFieldError?.field === field) {
      return apiFieldError.message;
    }
    return undefined;
  }

  function setValue<K extends FieldKey>(field: K, value: OrderFormValues[K]) {
    setValues((prev) => ({ ...prev, [field]: value }));
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const result = orderFormSchema.safeParse(values);
    if (!result.success) {
      const flattened = result.error.flatten().fieldErrors;
      const errors: FieldErrors = {};
      for (const [key, messages] of Object.entries(flattened)) {
        const first = messages?.[0];
        if (first) {
          errors[key as FieldKey] = first;
        }
      }
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    // En `edit`, enviar SOLO los campos editables (permiso granular): los no
    // editables se omiten del cuerpo (no se envían). En `create`, todo es editable.
    const parsed = result.data;
    const editable: Partial<OrderFormValues> = {};
    (Object.keys(parsed) as FieldKey[]).forEach((field) => {
      // `order_type` se controla aparte (solo si la sede permite láser).
      if (field === "order_type") {
        return;
      }
      // `amount_paid` solo es relevante con pago "abonado".
      if (field === "amount_paid" && parsed.payment_status !== "abonado") {
        return;
      }
      if (mode === "create" || canEditField(field)) {
        Object.assign(editable, { [field]: parsed[field] });
      }
    });
    if (showOrderType && (mode === "create" || canEditField("order_type"))) {
      editable.order_type = parsed.order_type;
    }
    onSubmit(editable);
  }

  const showAmountPaid = values.payment_status === "abonado";

  return (
    <form className="flex flex-col gap-5" onSubmit={handleSubmit} noValidate>
      <Field
        field="order_number"
        editable={mode === "create" || canEditField("order_number")}
        value={values.order_number}
        error={errorFor("order_number")}
        onChange={(v) => setValue("order_number", v)}
        inputMode="numeric"
        autoComplete="off"
      />
      <Field
        field="customer_name"
        editable={mode === "create" || canEditField("customer_name")}
        value={values.customer_name}
        error={errorFor("customer_name")}
        onChange={(v) => setValue("customer_name", v)}
      />
      <Field
        field="attendant"
        editable={mode === "create" || canEditField("attendant")}
        value={values.attendant}
        error={errorFor("attendant")}
        onChange={(v) => setValue("attendant", v)}
      />
      <Field
        field="sale_amount"
        editable={mode === "create" || canEditField("sale_amount")}
        value={values.sale_amount}
        error={errorFor("sale_amount")}
        onChange={(v) => setValue("sale_amount", v)}
        inputMode="decimal"
      />
      <div className="grid gap-5 sm:grid-cols-2">
        <Field
          field="invoice_date"
          type="date"
          editable={mode === "create" || canEditField("invoice_date")}
          value={values.invoice_date}
          error={errorFor("invoice_date")}
          onChange={(v) => setValue("invoice_date", v)}
        />
        <Field
          field="delivery_date"
          type="date"
          editable={mode === "create" || canEditField("delivery_date")}
          value={values.delivery_date}
          error={errorFor("delivery_date")}
          onChange={(v) => setValue("delivery_date", v)}
        />
      </div>
      <Field
        field="delivery_time"
        type="time"
        editable={mode === "create" || canEditField("delivery_time")}
        value={values.delivery_time}
        error={errorFor("delivery_time")}
        onChange={(v) => setValue("delivery_time", v)}
      />

      {/* Estado de pago (select) */}
      <div className="flex flex-col gap-1">
        <label htmlFor="field-payment_status" className="text-sm font-medium text-slate-700">
          {FIELD_LABEL.payment_status}
        </label>
        {mode === "create" || canEditField("payment_status") ? (
          <select
            id="field-payment_status"
            className={SELECT_CLASS}
            value={values.payment_status}
            aria-invalid={errorFor("payment_status") ? true : undefined}
            onChange={(event) =>
              setValue("payment_status", event.target.value as OrderFormValues["payment_status"])
            }
          >
            {PAYMENT_STATUS_VALUES.map((value) => (
              <option key={value} value={value}>
                {PAYMENT_LABEL[value]}
              </option>
            ))}
          </select>
        ) : (
          <p className={READONLY_CLASS}>{PAYMENT_LABEL[values.payment_status]}</p>
        )}
        <FieldError id="field-payment_status-error" message={errorFor("payment_status")} />
      </div>

      {/* Monto abonado: solo si el pago es "abonado" */}
      {showAmountPaid && (
        <Field
          field="amount_paid"
          editable={mode === "create" || canEditField("payment_status")}
          value={values.amount_paid ?? ""}
          error={errorFor("amount_paid")}
          onChange={(v) => setValue("amount_paid", v)}
          inputMode="decimal"
        />
      )}

      {/* Tipo de orden: solo si la sede permite láser */}
      {showOrderType && (
        <div className="flex flex-col gap-1">
          <label htmlFor="field-order_type" className="text-sm font-medium text-slate-700">
            {FIELD_LABEL.order_type}
          </label>
          {mode === "create" || canEditField("order_type") ? (
            <select
              id="field-order_type"
              className={SELECT_CLASS}
              value={values.order_type}
              onChange={(event) =>
                setValue("order_type", event.target.value as OrderFormValues["order_type"])
              }
            >
              {ORDER_TYPE_VALUES.map((value) => (
                <option key={value} value={value}>
                  {value === "laser" ? "Láser" : "Normal"}
                </option>
              ))}
            </select>
          ) : (
            <p className={READONLY_CLASS}>
              {values.order_type === "laser" ? "Láser" : "Normal"}
            </p>
          )}
        </div>
      )}

      {/* Descripción (textarea) */}
      <div className="flex flex-col gap-1">
        <label htmlFor="field-description" className="text-sm font-medium text-slate-700">
          {FIELD_LABEL.description}
        </label>
        {mode === "create" || canEditField("description") ? (
          <textarea
            id="field-description"
            className="block min-h-20 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            value={values.description ?? ""}
            onChange={(event) => setValue("description", event.target.value)}
          />
        ) : (
          <p className={READONLY_CLASS}>{values.description || "—"}</p>
        )}
      </div>

      {formLevelApiError && (
        <p role="alert" className="text-sm text-red-600">
          {formLevelApiError}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button type="submit" variant="primary" size="md" disabled={isSubmitting}>
          {isSubmitting ? "Guardando…" : submitLabel}
        </Button>
        <Button type="button" variant="secondary" size="md" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

/** Texto de error bajo un campo (o nada). */
function FieldError({ id, message }: { id: string; message?: string | undefined }) {
  if (!message) {
    return null;
  }
  return (
    <p id={id} className="text-sm text-red-600">
      {message}
    </p>
  );
}

interface FieldProps {
  field: Exclude<FieldKey, "payment_status" | "order_type" | "description">;
  value: string;
  editable: boolean;
  error?: string | undefined;
  onChange: (value: string) => void;
  type?: "text" | "date" | "time" | undefined;
  inputMode?: "numeric" | "decimal" | undefined;
  autoComplete?: string | undefined;
}

/** Campo de texto/fecha/hora con etiqueta, error y modo solo-lectura. */
function Field({
  field,
  value,
  editable,
  error,
  onChange,
  type = "text",
  inputMode,
  autoComplete,
}: FieldProps) {
  const inputId = `field-${field}`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-sm font-medium text-slate-700">
        {FIELD_LABEL[field]}
      </label>
      {editable ? (
        <Input
          id={inputId}
          type={type}
          value={value}
          inputMode={inputMode}
          autoComplete={autoComplete}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-error` : undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <p className={READONLY_CLASS}>{value || "—"}</p>
      )}
      <FieldError id={`${inputId}-error`} message={error} />
    </div>
  );
}
